/**
 * tin-can-alley.js — GDD 6.2.1. The exemplar stall.
 *
 * Three pyramids of tin cans at 2.5 m / 3.5 m / 4.5 m holding 3, 6 and 10 cans.
 * Three baseballs per round. A can scores only when it is knocked FULLY off its
 * shelf — a can that topples but stays on the plank is not a point, which is
 * what makes the far pyramid a real target rather than a splash-damage lottery.
 *
 * Clearing the mid pyramid with a single ball awards a small plush; clearing
 * the far pyramid with a single ball awards a large one (GDD 7.4). The
 * one-ball test is done by snapshotting each pyramid's knocked count when a
 * ball is thrown and comparing when it clears.
 *
 * Every tuning number lives in constants.js TIN_CAN_ALLEY.
 *
 * @module stalls/tin-can-alley
 */

import * as THREE from 'three';
import { TIN_CAN_ALLEY, PRIZE_KIND, HUB } from '../constants.js';
import { StallBase, registerStall } from './framework.js';
import * as factory from '../assets3d/factory.js';
import { createStallLight } from '../world/lighting.js';
import { audio } from '../audio/index.js';
import { haptics } from '../core/haptics.js';

const CFG = TIN_CAN_ALLEY;

class TinCanAlley extends StallBase {
  constructor(ctx) {
    super(ctx);
    /**
     * @type {Array<{ key: string, def: object, cans: object[], knocked: Set<number>,
     *                cleared: boolean, knockedAtThrow: number, centre: THREE.Vector3 }>}
     */
    this.pyramids = [];
    this._settleTimer = 0;
    this._awaitingSettle = false;
    this._ballAge = new Map();
  }

  // ── Static build ────────────────────────────────────────────────────────

  onBuild() {
    // A shelf per pyramid, plus a back wall so wild throws stop somewhere.
    for (const p of CFG.pyramids) {
      const width = 0.28 + p.rows * 0.24;
      const shelf = factory.createShelf(width, 0.4, CFG.shelfHeight);
      this.spawn(shelf, { x: 0, y: 0, z: p.distance }, {
        type: 'fixed', shape: 'box',
        halfExtents: { x: width / 2, y: 0.035, z: 0.2 },
        position: this.localToWorld(0, CFG.shelfHeight, p.distance),
        friction: 0.4,
      });

      const label = factory.createSign('', {
        w: 0.62, h: 0.2,
        lines: [`${p.pointsPerCan} PTS`],
        bg: '#12172a',
      });
      // Follows the stack, not the shelf — three labels stacked on one centre
      // line hide behind each other exactly as the pyramids used to.
      label.position.set(p.x ?? 0, CFG.shelfHeight - 0.22, p.distance - 0.21);
      label.rotation.y = Math.PI;
      this.root.add(label);
    }

    // Backstop so balls do not sail into the hub and litter the midway.
    // Canvas-coloured rather than near-black: a dark slab this size reads as a
    // hole in the booth rather than a backdrop.
    const stopW = HUB.boothWidth - 0.4;
    const backstop = new THREE.Mesh(
      new THREE.BoxGeometry(stopW, 2.2, 0.1),
      factory.mat(0x6d5f7a, { rough: 0.95 }),
    );
    backstop.receiveShadow = true;
    this.spawn(backstop, { x: 0, y: 1.1, z: CFG.pyramids[2].distance + 1.0 }, {
      type: 'fixed', shape: 'box',
      halfExtents: { x: stopW / 2, y: 1.1, z: 0.05 },
      position: this.localToWorld(0, 1.1, CFG.pyramids[2].distance + 1.0),
      restitution: 0.05, friction: 0.9,
    });

    // Floor plane inside the booth, so cans and balls land on something.
    const floor = new THREE.Mesh(
      new THREE.BoxGeometry(stopW, 0.1, 6.4),
      factory.mat(0x6a5a44, { rough: 1 }),
    );
    floor.receiveShadow = true;
    this.spawn(floor, { x: 0, y: -0.05, z: 2.6 }, {
      type: 'fixed', shape: 'box',
      halfExtents: { x: stopW / 2, y: 0.05, z: 3.2 },
      position: this.localToWorld(0, -0.05, 2.6),
      friction: 0.8, restitution: 0.15,
    });

    // Chest height and slightly forward: within the 0.45 m grab radius of a
    // desktop hand (which hovers around y 1.34, z 0.45) AND high enough to sit
    // inside the frame — a player must be able to see the balls they are meant
    // to pick up without hunting for them.
    this.buildTray({ width: 0.78, y: 1.20, z: 0.72 });
    // Far enough back to sit inside the frame above the pyramids rather than
    // filling the top of the view.
    this.buildScoreboard({ y: 2.25, z: 3.4 });

    const light = createStallLight(0xffe0b0, 2.0, 11);
    light.position.set(0, 3.0, 2.6);
    this.root.add(light);
  }

  // ── Round setup ─────────────────────────────────────────────────────────

  setupRound() {
    this.setRoundFormat({ attempts: CFG.ballsPerRound });
    this.pyramids = [];
    this._awaitingSettle = false;
    this._settleTimer = 0;
    this._ballAge.clear();

    for (const def of CFG.pyramids) {
      this.pyramids.push({
        key: def.key,
        def,
        cans: this._buildPyramid(def),
        knocked: new Set(),
        cleared: false,
        knockedAtThrow: 0,
        centre: this.localToWorld(def.x ?? 0, CFG.shelfHeight + 0.2, def.distance),
      });
    }

    this.dispense(CFG.ballsPerRound, () => factory.createBall('baseball'), {
      y: 1.27, z: 0.72, spacing: 0.16,
      desc: { ccd: true },
    });
  }

  /**
   * Stack a pyramid of cans on a shelf.
   *
   * `def.x` slides the whole stack along its own plank (the shelf itself never
   * moves) so the three pyramids read separately down the alley. Row spacing,
   * count and height are untouched.
   *
   * @param {{ cans: number, rows: number, distance: number, x?: number }} def
   * @returns {object[]} Can handles.
   * @private
   */
  _buildPyramid(def) {
    const r = CFG.canRadius;
    const h = CFG.canHeight;
    const spacingX = r * 2 + 0.012;
    const baseY = CFG.shelfHeight + h / 2 + 0.004;
    const originX = def.x ?? 0;

    const handles = [];
    let placed = 0;
    for (let row = 0; row < def.rows && placed < def.cans; row++) {
      const inRow = Math.min(def.rows - row, def.cans - placed);
      for (let i = 0; i < inRow; i++) {
        const x = originX + (i - (inRow - 1) / 2) * spacingX;
        const y = baseY + row * (h + 0.002);
        const can = factory.createCan();
        const handle = this.spawn(can, { x, y, z: def.distance }, {
          position: this.localToWorld(x, y, def.distance),
          mass: CFG.canMass,
          friction: CFG.canFriction,
          restitution: CFG.canRestitution,
          angularDamping: 0.15,
        });
        handle.userData.pyramid = def.key;
        handle.userData.canIndex = handles.length;
        handle.userData.restY = y;
        // Exposed to tests as `__cc.stalls.targets()` grouped by pyramid.
        this.registerTarget(handle, { group: def.key });
        handles.push(handle);
        placed++;
      }
    }
    return handles;
  }

  // ── Per-frame ───────────────────────────────────────────────────────────

  onUpdate(dt) {
    this._checkKnockedCans();
    this._recycleBalls(dt);
    this._checkRoundEnd(dt);
  }

  /**
   * A can counts as knocked off once its centre drops below the shelf plane by
   * `offShelfDrop`, or it leaves the pyramid's zone laterally.
   * @private
   */
  _checkKnockedCans() {
    for (const py of this.pyramids) {
      if (py.cleared) continue;

      for (let i = 0; i < py.cans.length; i++) {
        if (py.knocked.has(i)) continue;
        const handle = py.cans[i];
        if (handle.removed) continue;

        const world = handle.position();
        const local = this.worldToLocal(world);
        // Measured against the SHELF PLANE, not the can's own rest height: a
        // can that merely topples still sits above this, so only cans that
        // actually leave the plank score (GDD 6.2.1).
        const fellBelow = local.y < CFG.shelfHeight - CFG.offShelfDrop;
        const leftZone = Math.abs(local.x) > CFG.offShelfRadius
          || Math.abs(local.z - py.def.distance) > CFG.offShelfRadius;

        if (!fellBelow && !leftZone) continue;

        py.knocked.add(i);
        handle.userData.scored = true;
        this.addScore(py.def.pointsPerCan, {
          position: world,
          label: `${py.key} can`,
        });
        audio.play('impact-hit', { volume: 0.8 });
        haptics.play('right', 'impact', { force: 12 });

        if (py.knocked.size === py.cans.length) this._onPyramidCleared(py);
      }
    }
  }

  /**
   * Award the clear bonus, and a direct prize if it took a single ball.
   * @private
   */
  _onPyramidCleared(py) {
    py.cleared = true;
    this.addScore(py.def.clearBonus, {
      position: py.centre,
      label: `${py.key} pyramid cleared`,
      color: '#6fe7c4',
    });
    audio.play('prize-earned');

    // knockedAtThrow is snapshotted when each ball leaves the hand, so a zero
    // here means this whole pyramid went down on the ball currently in flight.
    const oneBall = py.knockedAtThrow === 0;
    if (oneBall && py.def.directPrize) {
      const kind = py.def.directPrize === 'plush-large'
        ? PRIZE_KIND.PLUSH_LARGE
        : PRIZE_KIND.PLUSH_SMALL;
      this.awardDirectPrize(kind);
      this._showPrize(kind, py.centre);
    }
  }

  /** Pop the won plush up on the counter so the win is visible. @private */
  _showPrize(kind, worldPos) {
    const plush = factory.createPlush(kind === PRIZE_KIND.PLUSH_LARGE ? 'large' : 'small');
    const local = this.worldToLocal(worldPos.clone());
    plush.position.set(local.x, CFG.shelfHeight + 0.35, local.z);
    plush.scale.setScalar(0.01);
    this.root.add(plush);
    let t = 0;
    const grow = (dt) => {
      t += dt;
      const k = Math.min(1, t / 0.45);
      plush.scale.setScalar(k * (kind === PRIZE_KIND.PLUSH_LARGE ? 1.5 : 1));
      plush.rotation.y += dt * 2.2;
      return t < 6;
    };
    this._prizeAnimations = this._prizeAnimations || [];
    this._prizeAnimations.push(grow);
  }

  /**
   * Snapshot pyramid state when a ball is thrown, so one-ball clears can be
   * detected, and start ageing the ball for recycling.
   * @private
   */
  _onThrowableReleased(handle) {
    for (const py of this.pyramids) py.knockedAtThrow = py.knocked.size;
    this._ballAge.set(handle.id, 0);
    super._onThrowableReleased(handle);
  }

  /** @private */
  _recycleBalls(dt) {
    for (const [id, age] of [...this._ballAge]) {
      const next = age + dt;
      this._ballAge.set(id, next);
      if (next < CFG.ballLifetime) continue;
      const handle = this.throwables.find((h) => h.id === id);
      this._ballAge.delete(id);
      if (handle && !handle.removed) handle.freeze();
    }
    if (this._prizeAnimations) {
      this._prizeAnimations = this._prizeAnimations.filter((fn) => fn(dt));
    }
  }

  /**
   * The round ends when every pyramid is down, or once the last ball has been
   * thrown and everything has settled.
   * @private
   */
  _checkRoundEnd(dt) {
    if (!this.roundActive) return;

    if (this.pyramids.length > 0 && this.pyramids.every((p) => p.cleared)) {
      this.endRound();
      return;
    }

    if (this.attemptsLeft > 0) return;

    this._awaitingSettle = true;
    this._settleTimer += dt;
    if (this._settleTimer >= CFG.settleTime) this.endRound();
  }

  // ── Aim assist ──────────────────────────────────────────────────────────

  /**
   * Nudge throws toward the nearest standing can (5° cone, GDD 5.4).
   * @param {THREE.Vector3} releasePos
   * @returns {{position: THREE.Vector3}|null}
   */
  findAimTarget(releasePos) {
    let best = null;
    let bestD = Infinity;
    for (const py of this.pyramids) {
      if (py.cleared) continue;
      for (let i = 0; i < py.cans.length; i++) {
        if (py.knocked.has(i)) continue;
        const handle = py.cans[i];
        if (handle.removed) continue;
        const p = handle.position();
        const d = p.distanceTo(releasePos);
        if (d < bestD) { bestD = d; best = p; }
      }
    }
    return best ? { position: best } : null;
  }

  // ── Scoreboard ──────────────────────────────────────────────────────────

  updateScoreboard() {
    if (!this.scoreboard) return;
    const cleared = this.pyramids.filter((p) => p.cleared).length;
    const down = this.pyramids.reduce((n, p) => n + p.knocked.size, 0);
    const total = this.pyramids.reduce((n, p) => n + p.cans.length, 0);
    this.scoreboard.setLines([
      `Score   ${this.score}`,
      `Balls   ${this.attemptsLeft} / ${this.maxAttempts}`,
      `Cans    ${down} / ${total}`,
      `Cleared ${cleared} / 3`,
    ]);
  }

  onTeardown() {
    this.pyramids = [];
    this._ballAge.clear();
    this._prizeAnimations = null;
  }
}

registerStall({
  id: 'tin-can-alley',
  name: 'Tin Can Alley',
  cost: CFG.cost,
  create: (ctx) => new TinCanAlley(ctx),
});

export { TinCanAlley };
